import { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import Icon from './Icon';

export interface DemoEvent {
  id: string;
  timestamp: Date;
  type: 'info' | 'bid' | 'outbid' | 'warning' | 'success' | 'error' | 'system';
  icon: string;
  title: string;
  description?: string;
  amount?: number;
  actor?: string;
}

interface EventLogProps {
  events: DemoEvent[];
  maxHeight?: string;
  title?: string;
  showTimestamp?: boolean;
}

const typeStyles: Record<DemoEvent['type'], { bg: string; icon: string; text: string }> = {
  info: { bg: 'bg-blue-50', icon: 'text-blue-600', text: 'text-blue-900' },
  bid: { bg: 'bg-green-50', icon: 'text-green-600', text: 'text-green-900' },
  outbid: { bg: 'bg-orange-50', icon: 'text-orange-600', text: 'text-orange-900' },
  warning: { bg: 'bg-amber-50', icon: 'text-amber-600', text: 'text-amber-900' },
  success: { bg: 'bg-emerald-50', icon: 'text-emerald-600', text: 'text-emerald-900' },
  error: { bg: 'bg-red-50', icon: 'text-red-600', text: 'text-red-900' },
  system: { bg: 'bg-gray-50', icon: 'text-gray-600', text: 'text-gray-900' },
};

export default function EventLog({ 
  events, 
  maxHeight = '400px',
  title = 'Event Log',
  showTimestamp = true,
}: EventLogProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events, autoScroll]);

  // Detect manual scroll
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="list_alt" size="small" className="text-gray-600" />
          <h3 className="font-medium text-gray-900">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{events.length} events</span>
          {!autoScroll && (
            <button
              onClick={() => {
                setAutoScroll(true);
                if (scrollRef.current) {
                  scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                }
              }}
              className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              <Icon name="arrow_downward" size="small" />
              Jump to latest
            </button>
          )}
        </div>
      </div>

      {/* Event List */}
      <div 
        ref={scrollRef}
        onScroll={handleScroll}
        className="overflow-y-auto"
        style={{ maxHeight }}
      >
        {events.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Icon name="hourglass_empty" size="large" className="mb-2 opacity-50" />
            <p>Waiting for events...</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {events.map((event, index) => {
              const style = typeStyles[event.type];
              const isLatest = index === events.length - 1;

              return (
                <div
                  key={event.id}
                  className={clsx(
                    'px-4 py-3 flex gap-3 transition-colors',
                    style.bg,
                    isLatest && 'animate-pulse-once'
                  )}
                >
                  {/* Icon */}
                  <div className={clsx('flex-shrink-0 mt-0.5', style.icon)}>
                    <Icon name={event.icon} size="small" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={clsx('font-medium text-sm', style.text)}>
                        {event.title}
                      </p>
                      {showTimestamp && (
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatTime(event.timestamp)}
                        </span>
                      )}
                    </div>
                    {event.description && (
                      <p className="text-sm text-gray-600 mt-0.5">
                        {event.description}
                      </p>
                    )}
                    {event.amount !== undefined && (
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        £{(event.amount / 100).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Live indicator */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
        <span className="text-xs text-gray-500">Live updates</span>
      </div>
    </div>
  );
}
